import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AgentSettingsInput,
  GuardrailsTraceMaskingMode,
  PhoenixTraceProviderSettings,
  TraceProviderModel,
  TraceProviderNames,
  TraceSettings,
} from '../../../gamma-api';
import IbTypography from '../common/IbTypography';
import IbButton from '../common/IbButton';
import IbInput from '../common/IbInput';
import IbSwitch from '../common/IbSwitch';
import { CheckFieldValueFnMap, useValidationForm } from '../../utils/validationUtil';
import IbScrollSpy from '../common/IbScrollSpy';

import {
  MAIN_CLASS_NAME,
  CONTENT_CLASS_NAME,
  BLOCK_CLASS_NAME,
  BLOCK_ITEM_CLASS_NAME,
  BLOCK_TITLE_CLASS_NAME,
  FOOTER_CLASS_NAME,
} from './const';

type TracingPipelineSettingsForm = {
  enabled: boolean | undefined;
  api_base: string | undefined;
  project_name: string | undefined;
};

export interface ITracingProps {
  agentSettings?: AgentSettingsInput;
  traceSettings?: TraceSettings;
  traceProvider?: TraceProviderModel;
  onAgentSettingsChange?: (settings: AgentSettingsInput) => void;
  onTraceSettingsChange?: (traceSettings?: TraceSettings, traceProvider?: TraceProviderModel) => void;
  onSave?: () => Promise<void>;
}

const Tracing: React.FC<ITracingProps> = ({
  agentSettings,
  traceSettings,
  traceProvider,
  onAgentSettingsChange,
  onTraceSettingsChange,
  onSave,
}) => {
  const { t } = useTranslation();

  const checkFieldValueFnMap: CheckFieldValueFnMap<TracingPipelineSettingsForm> = {
    enabled: () => true,
    api_base: (value: string | undefined, allValues: TracingPipelineSettingsForm) => !allValues.enabled || !!value,
    project_name: (value: string | undefined, allValues: TracingPipelineSettingsForm) => !allValues.enabled || !!value,
  };

  const initialValues: TracingPipelineSettingsForm = {
    enabled: traceProvider?.enabled,
    api_base: traceProvider?.settings.api_base ?? undefined,
    project_name: traceSettings?.project_name ?? undefined,
  };

  const {
    validationState,
    updateFormValues,
    onFieldChange,
    onFieldBlur,
    validate,
    getShowError,
    getErrorMessage,
  } = useValidationForm<TracingPipelineSettingsForm>(initialValues, checkFieldValueFnMap);

  const [saving, setSaving] = useState(false);

  const onSettingsUpdated = () => {
    updateFormValues(initialValues);
  };

  useEffect(onSettingsUpdated, [traceProvider, traceSettings]);

  const onTraceProviderEnabledChange = () => {
    onTraceSettingsChange?.(traceSettings, {
      ...traceProvider,
      enabled: !traceProvider?.enabled,
      name: TraceProviderNames.Phoenix,
      settings: {
        ...traceProvider?.settings,
      } as PhoenixTraceProviderSettings,
    } as TraceProviderModel);

    onFieldChange('enabled', !traceProvider?.enabled);
  };

  const onApiBaseChange = (value: string) => {
    onTraceSettingsChange?.(traceSettings, {
      ...traceProvider,
      settings: {
        ...traceProvider?.settings,
        api_base: value,
      },
    } as TraceProviderModel);

    onFieldChange('api_base', value);
  };

  const onApiBaseBlur = () => onFieldBlur('api_base');

  const onProjectNameChange = (value: string) => {
    onTraceSettingsChange?.({ ...traceSettings, project_name: value } as TraceSettings, traceProvider);
    onFieldChange('project_name', value);
  };

  const onProjectNameBlur = () => onFieldBlur('project_name');

  const onGuardrailsTraceMaskingChange = (checked: boolean) => {
    if (!agentSettings) {
      return;
    }

    onAgentSettingsChange?.({
      ...agentSettings,
      guardrails: {
        ...agentSettings.guardrails,
        trace_masking: checked ? GuardrailsTraceMaskingMode.Enabled : GuardrailsTraceMaskingMode.Disabled,
      },
    } as AgentSettingsInput);
  };

  const onSaveButtonClick = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    await onSave?.();
    setSaving(false);
  };

  const renderPhoenixBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>Arize Phoenix</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={traceProvider?.enabled || false} size="small" onChange={onTraceProviderEnabledChange} />
          <IbTypography.Paragraph strong>{t('Settings: Enable Arize Phoenix')}</IbTypography.Paragraph>
        </IbTypography>
      </div>
      {traceProvider?.enabled && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              Phoenix URL
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">{t('Settings: Phoenix instance address.')}</IbTypography.Paragraph>
            <IbInput
              placeholder="https://phoenix-server.com/v1/traces"
              status={getShowError(validationState.api_base) ? 'error' : 'default'}
              value={traceProvider.settings.api_base}
              onBlur={onApiBaseBlur}
              onChange={onApiBaseChange}
            />
            {getShowError(validationState.api_base) && (
              <IbTypography.Paragraph error type="descriptor">
                {getErrorMessage(validationState.api_base, t('Settings: Tracing: API Base: Error'))}
              </IbTypography.Paragraph>
            )}
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Project ID')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t(
                'Settings: Before you start tracing in Phoenix, you need to specify the name of the project in which the traces will be saved.'
              )}
            </IbTypography.Paragraph>
            <IbInput
              status={getShowError(validationState.project_name) ? 'error' : 'default'}
              value={traceSettings?.project_name}
              onBlur={onProjectNameBlur}
              onChange={onProjectNameChange}
            />
            {getShowError(validationState.project_name) && (
              <IbTypography.Paragraph error type="descriptor">
                {getErrorMessage(validationState.project_name, t('Settings: Tracing: Project Name: Error'))}
              </IbTypography.Paragraph>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderTracingGuardrailsBlock = () => {
    if (!agentSettings) {
      return null;
    }

    const maskTracesEnabled =
      (agentSettings.guardrails?.trace_masking ?? GuardrailsTraceMaskingMode.Enabled) ===
      GuardrailsTraceMaskingMode.Enabled;

    return (
      <div className={BLOCK_CLASS_NAME}>
        <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
          <h4>{t('Agent: Guardrails: Tracing: Title')}</h4>
        </IbTypography>
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography>
            <IbSwitch checked={maskTracesEnabled} size="small" onChange={onGuardrailsTraceMaskingChange} />
            <IbTypography.Paragraph strong>
              {t('Agent: Guardrails: Masking: Mask traces: Title')}
            </IbTypography.Paragraph>
          </IbTypography>
          <IbTypography.Paragraph type="secondary">
            {t('Agent: Guardrails: Masking: Mask traces: Description')}
          </IbTypography.Paragraph>
        </div>
      </div>
    );
  };

  const sections = [
    ...(agentSettings
      ? [
          {
            id: 'tracing-guardrails',
            title: t('Agent: Guardrails: Tracing: Title'),
            content: renderTracingGuardrailsBlock(),
          },
        ]
      : []),
    { id: 'arize-phoenix', title: 'Arize Phoenix', content: renderPhoenixBlock() },
  ];

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={CONTENT_CLASS_NAME}>
        <IbScrollSpy displayNav sections={sections} />
      </div>
      <div className={FOOTER_CLASS_NAME}>
        <IbButton disabled={saving} onClick={onSaveButtonClick}>
          {t('Settings: Save')}
        </IbButton>
      </div>
    </div>
  );
};

export default Tracing;
